import {
  ALLOWED_DATA_SOURCES,
  DataSourceNotAllowedError,
  isOriginTrusted,
  matchesPattern,
  resolveDataSourceUrl,
  type DataSourceService,
} from '@de/config/allowed-data-sources';
import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { isHttpDataSourceDeclaration } from '@de/utils/data-sources/data-source-kind';
import type { ApiConfig } from '@de/types/config';

/**
 * Auto-discover the {@link DataSourceService} that owns a given path
 * pattern, using the static allowlist as the source of truth. The
 * allowlist is curated so paths are unique across services (offerings,
 * offeringsService, organizations, deliverySystem, reporting). Deprecated
 * empty groups (campaignManagement, mobius, sameOrigin) contribute no
 * entries — a path either matches one active service or none, never both.
 *
 * Only the path portion is examined: callers pass either a leading-slash
 * path (`/v1/search`) or an already-absolute URL whose pathname is
 * extracted by `resolveDataSourceUrl()` downstream. Methods are matched
 * case-insensitively.
 *
 * Returns `null` when no allowlist entry matches; the caller decides
 * whether to leave the declaration alone (legacy/standalone host) or
 * surface an error.
 */
export function findServiceForPath(
  path: string,
  method: string,
): DataSourceService | null {
  const normalizedMethod = method.toUpperCase();
  for (const [service, sources] of Object.entries(ALLOWED_DATA_SOURCES)) {
    for (const source of sources) {
      if (
        (source.methods as readonly string[]).includes(normalizedMethod) &&
        matchesPattern(extractPath(path), source.pattern)
      ) {
        return service as DataSourceService;
      }
    }
  }
  return null;
}

/**
 * Normalize the bridge's `dataSources` declarations against `apiConfig`
 * so the {@link DataSourceManagerProvider} only ever sees fully-resolved,
 * security-checked endpoints.
 *
 * Pipeline per declaration (review re-look #1, R. Vazquez --
 * "ConfigurableStep needs to derive trusted service origins/API base
 * URLs and normalize declarations before passing them to DSM"):
 *
 *   1. Allowlisted endpoints (`/v1/...`, `https://service/v1/...`), including
 *      templated paths (`/v3/schema/{{context.schoolId}}`):
 *        a. Look up the owning service via {@link findServiceForPath}.
 *           The matcher only inspects the leading prefix of the path,
 *           so a wildcard allowlist entry (`/v3/schema/*`) still binds
 *           a templated path to its owning service before interpolation.
 *        b. Resolve to a full URL via {@link resolveDataSourceUrl}
 *           using the host's `apiConfig`, even when the author supplied
 *           an absolute URL. This enforces the allowlist's service owner
 *           (for example, dimensions always goes to Offerings Service, not
 *           the legacy Offerings host). The template is preserved verbatim
 *           in the resulting absolute URL so DSM can still interpolate it
 *           at fetch time and re-check the post-interpolation path against
 *           the allowlist.
 *        c. Replace `decl.endpoint` with the resolved URL.
 *      Unknown paths (templated or not) are left alone -- DSM's
 *      allowlist will reject them at fetch time with the expected
 *      `DataSourceEndpointNotAllowedError`.
 *
 * Errors are caught and surfaced via `console.warn` so a single bad
 * declaration cannot take down the entire bridge render. The offending
 * entry is dropped from the normalized record (DSM won't fetch it),
 * matching the existing fail-closed posture for unresolvable sources.
 */
export function resolveDataSourceDeclarations(
  declarations: Record<string, DataSourceDeclaration>,
  apiConfig: ApiConfig,
  tenantId?: string,
): Record<string, DataSourceDeclaration> {
  const out: Record<string, DataSourceDeclaration> = {};
  for (const [name, decl] of Object.entries(declarations)) {
    if (!isHttpDataSourceDeclaration(decl)) {
      out[name] = decl;
      continue;
    }
    const endpoint = decl.endpoint;
    if (typeof endpoint !== 'string' || endpoint.length === 0) {
      out[name] = decl;
      continue;
    }
    // Templated endpoints (`/v3/schema/{{context.schoolId}}`)
    // flow through the same resolution path as fully-literal paths.
    // `findServiceForPath` and `resolveDataSourceUrl` both match by
    // prefix-or-exact against the static allowlist, and the `{{...}}`
    // characters never appear in any allowlist pattern -- so a
    // templated path matches iff there is a wildcard entry whose
    // prefix is fully literal (e.g. `/v3/schema/*`). The template is
    // preserved verbatim in the resolved absolute URL; DSM still
    // interpolates it at fetch time and re-checks the post-interpolation
    // path against the allowlist before issuing the request.
    const service = findServiceForPath(endpoint, decl.method);
    if (!service) {
      out[name] = decl;
      continue;
    }
    try {
      const resolvedUrl = resolveDataSourceUrl(
        service,
        endpoint,
        decl.method,
        apiConfig,
        tenantId,
      );
      out[name] = { ...decl, endpoint: resolvedUrl };
    } catch (err) {
      if (err instanceof DataSourceNotAllowedError) {
        // eslint-disable-next-line no-console
        console.warn(
          `[ConfigurableStep] dropping dataSources["${name}"]: ${err.message}`,
        );
        continue;
      }
      throw err;
    }
  }
  return out;
}

const SERVICE_BASE_URL_KEYS: ReadonlyArray<keyof ApiConfig> = [
  'offeringsServiceBaseUrl',
  'offeringsSearchApiUrl',
  'organizationServiceBaseUrl',
  'organizationApiUrl',
  'deliverySystemBaseUrl',
  'deliverySystemApiUrl',
  'campaignManagementApiUrl',
  'mobiusApiUrl',
  'reportingApiUrl',
  'productApiBffOrigin',
];

const REPORTING_QUERY_PATH = '/api/reporting/v1/query';

/**
 * Resolve the absolute URL DSM uses for `kind: 'reportingModel'` sources.
 * Prefers `apiConfig.reportingApiUrl` (site/BFF origin); falls back to
 * `sameOriginBase` so the dev harness can target its Vite BFF proxy.
 */
export function deriveReportingQueryUrl(
  apiConfig: ApiConfig,
  options?: { readonly sameOriginBase?: string },
): string | undefined {
  const configured = apiConfig.reportingApiUrl?.trim();
  const base = configured || options?.sameOriginBase?.trim();
  if (!base) return undefined;
  return `${base.replace(/\/$/, '')}${REPORTING_QUERY_PATH}`;
}

/**
 * Ensure the reporting query origin is trusted when DSM posts to an
 * absolute same-origin BFF URL (common in local preview).
 */
export function appendReportingQueryOrigin(
  origins: readonly string[],
  reportingQueryUrl: string | undefined,
): readonly string[] {
  if (!reportingQueryUrl) return origins;
  try {
    const origin = new URL(reportingQueryUrl).origin.toLowerCase();
    if (origins.some((entry) => entry.toLowerCase() === origin)) {
      return origins;
    }
    return [...origins, origin];
  } catch {
    return origins;
  }
}

/**
 * Build the `trustedOrigins` set DSM uses to gate absolute-URL
 * declarations. Includes every configured service base URL on
 * `apiConfig`, normalized to its `origin` (scheme + host + port).
 *
 * Path-only declarations bypass {@link isOriginTrusted}, so this set
 * only matters when the DSL author writes an absolute URL or after
 * {@link resolveDataSourceDeclarations} promotes a path-only entry.
 *
 * Only `http:` and `https:` origins survive normalization -- anything
 * else is rejected by `isOriginTrusted` itself, so we don't bother
 * including non-network bases.
 */
export function deriveTrustedOriginsFromApiConfig(
  apiConfig: ApiConfig,
): readonly string[] {
  const origins = new Set<string>();
  for (const key of SERVICE_BASE_URL_KEYS) {
    const raw = apiConfig[key];
    if (typeof raw !== 'string' || raw.length === 0) continue;
    try {
      const parsed = new URL(raw);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        continue;
      }
      origins.add(parsed.origin.toLowerCase());
    } catch {
      // Drop malformed entries silently -- callers will see the same
      // fail-closed behavior they'd get if the field were absent.
    }
  }
  return Array.from(origins);
}

// Re-export so callers can validate ad-hoc strings against the same
// rule the bridge uses for its declarations (used by the integration
// test to assert the trusted-origin contract).
export { isOriginTrusted };

function extractPath(endpoint: string): string {
  if (!endpoint) return '';
  if (endpoint.startsWith('/')) return endpoint.split('?')[0] ?? '';
  try {
    const parsed = new URL(endpoint);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return parsed.pathname;
  } catch {
    return '';
  }
}
